import { format, isValid, parse } from 'date-fns';
import moment from 'moment';

const US_DATE_FORMAT = 'MM/DD/YYYY';
const US_DATETIME_FORMAT = 'MM/DD/YYYY h:mm:ss A';
const SERVER_DATE_FORMAT = 'YYYY-MM-DD';

const getCurrentDateString = (): string => {
  const date = new Date();
  return format(date, 'yyyy-MM-dd');
};

const getCurrentDateTimeString = (): string =>
  format(new Date(), 'yyyy-MM-dd_HH-mm-ss');

const getDateString = (date: Date): string => {
  try {
    return format(date, 'yyyy-MM-dd');
  } catch (e) {
    return '';
  }
};

const parseDateString = (
  dateString: string | null | undefined,
  format: string = 'yyyy-MM-dd'
): Date | null => {
  if (!dateString) {
    return null;
  }

  try {
    const parsedDate = parse(dateString, format, new Date());
    return isValid(parsedDate) ? parsedDate : null;
  } catch (e) {
    return null;
  }
};

// This function formats the date and time to UTC format, time is optional
function parseUTCdateTime(
  date: string | Date | undefined,
  includeTimeStamp: boolean = false
): string {
  if (date) {
    return moment
      .utc(date)
      .format(includeTimeStamp ? 'M/DD/YYYY hh:mm A' : 'MM/DD/YYYY');
  }
  return '';
}

const parseMomentUtc = (date: Parameters<typeof moment.utc>[0]) =>
  moment.utc(date);

// This function formats the date and time to local timezone format
function parseLocalDateTime(
  date: string | Date | undefined,
  includeTimeStamp: boolean = false
): string {
  if (date) {
    return moment(date).format(
      includeTimeStamp ? 'M/DD/YYYY hh:mm A' : 'MM/DD/YYYY'
    );
  }
  return '';
}

const isISOTimestamp = (value: string): boolean => {
  const isoTimestampRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
  return isoTimestampRegex.test(value) && moment.utc(value).isValid();
};

const isDateString = (value: string): boolean => {
  return moment(value, SERVER_DATE_FORMAT, true).isValid();
};

// A four-digit calendar year (1000–9999).
const isValidYear = (value: string | number): boolean => {
  const year = Number(value);
  return Number.isInteger(year) && year >= 1000 && year <= 9999;
};

const getLocalTimezoneAbbreviation = (): string => {
  return (
    new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value ?? ''
  );
};

const formatTcWorkspaceDate = (iso: string | null | undefined): string => {
  if (!iso) {
    return '—';
  }
  const m = moment(iso);
  return m.isValid() ? m.format('MMM D, YYYY') : iso;
};

// Formats a Date as a `YYYY-MM-DD` param using the *local* calendar day —
// `.toISOString()` would roll DatePicker local-midnight Dates back a day in
// UTC+ timezones.
const toDateParam = (date: Date): string => getDateString(date);

// `YYYY-MM-DD` param -> `MM/DD/YYYY` display string for a controlled Carbon
// DatePicker `value` — a stable string (not a fresh Date each render) keeps the
// picker from churning and clearing the input on blur.
const fromDateParam = (value: string | null | undefined): string => {
  if (!value) return '';
  const parsed = moment(value, SERVER_DATE_FORMAT);
  // A bound that is not a date param has nothing to display. Without this,
  // moment formats it as the literal string 'Invalid date', which is truthy and
  // ends up rendered in the picker input and on the applied-filter chip.
  return parsed.isValid() ? parsed.format(US_DATE_FORMAT) : '';
};

/**
 * A date the user TYPED into a date picker, as `yyyy-MM-dd`.
 *
 * Typed text is judged here rather than taken from the picker: a click outside re-parses
 * it without emitting `onChange`, so a caller listening only to `onChange` drops it, and
 * Carbon's parse for the m/d/Y format coerces rather than refuses (an out-of-range month
 * or day becomes 1).
 *
 * Returns undefined for anything that is not a whole valid date, so half-typed text
 * ("06/3") commits nothing rather than a date the user did not mean.
 *
 * @param {string} text - Raw input text, as typed.
 * @return {string | undefined} `yyyy-MM-dd` for a complete date, else undefined.
 */
const parseTypedDate = (text: string): string | undefined => {
  // The shape is checked before parsing because date-fns reads a partial year as a real
  // one — mid-typing "06/30/20" parses to the year 20, and committing that would put a
  // date two millennia early. Requiring four year digits is what makes every keystroke
  // on the way to a full date a no-op. Single-digit month and day are accepted because
  // people type them ("6/3/2027"), even though the picker itself writes 06/03/2027.
  const shape = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text.trim());
  if (!shape) return undefined;

  const [, month, day, year] = shape;
  // Four digits is not yet a year: a transposed "0227" for 2027 would commit year 227.
  if (!isValidYear(year)) return undefined;

  const parsed = parse(
    `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`,
    'MM/dd/yyyy',
    new Date()
  );

  return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : undefined;
};

export {
  getCurrentDateString,
  getCurrentDateTimeString,
  getDateString,
  parseDateString,
  parseUTCdateTime,
  parseLocalDateTime,
  parseMomentUtc,
  isISOTimestamp,
  isDateString,
  isValidYear,
  getLocalTimezoneAbbreviation,
  formatTcWorkspaceDate,
  toDateParam,
  fromDateParam,
  parseTypedDate,
  US_DATE_FORMAT,
  US_DATETIME_FORMAT,
  SERVER_DATE_FORMAT,
};
